import { useEffect, useRef, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Separator } from 'src/@/components/ui/separator';
import { Textarea } from 'src/@/components/ui/textarea';
import useAuth from 'src/hooks/useAuth';
import getActionListaUsuariosByClienteId from 'src/actions/usuarios/getActionListaUsuariosByClienteId';
import v2_actionListSectorsByClient from 'src/actions/v2/cliente/v2_actionListSectorsByClient';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const HINT_CLS = 'text-xs text-gray-400';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

// Mantenha em sincronia com lais-ai/src/agents/realestate/identity.js → DEFAULT_AGENT_CONFIG.transferencia
const GATILHOS_PREDEFINIDOS = [
    {
        value: 'cliente_interessado',
        title: 'Cliente gostou de um imóvel',
        description: 'Quando o cliente demonstra interesse em um imóvel apresentado (link, código ou sugestão).',
        icon: 'mdi:heart-outline',
    },
    {
        value: 'quer_atendente',
        title: 'Cliente pediu atendente humano',
        description: 'Quando o cliente diz "quero falar com alguém", "tem um corretor?", etc.',
        icon: 'mdi:account-tie-outline',
    },
    {
        value: 'sem_imoveis',
        title: 'Não encontrei imóveis',
        description: 'Quando a busca esgota tentativas de relaxamento sem encontrar opções no perfil pedido.',
        icon: 'mdi:home-search-outline',
    },
    {
        value: 'transfer_broker',
        title: 'Cliente quer mais opções (após N rodadas)',
        description: 'Quando o cliente continua pedindo "outras opções" após o limite de rodadas e a Lais envia o link completo.',
        icon: 'mdi:format-list-bulleted',
    },
];

const GATILHOS_VALORES = GATILHOS_PREDEFINIDOS.map((g) => g.value);

function novoIdGrupo() {
    return `grp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function novoIdGatilhoCustom() {
    return `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function grupoVazio() {
    return {
        id: novoIdGrupo(),
        nome: 'Novo grupo',
        modo: 'sector',
        setor_id: null,
        usuario_destino_id: null,
        gatilhos: [],
        gatilhosCustom: [],
    };
}

export default function TransferenciaSection({ aiAgent, changeModuleConfig }) {
    const { user } = useAuth();
    const [setores, setSetores] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const migracaoRef = useRef(false);

    const transferencia = aiAgent?.config?.module?.realstate?.transferencia || {};
    const modo = transferencia.modo || 'sector';
    const setorId = transferencia.setor_id || '';
    const usuarioId = transferencia.usuario_destino_id || '';
    const fallback = transferencia.fallback === 'skip' ? 'skip' : 'default';
    const grupos = Array.isArray(transferencia.grupos) ? transferencia.grupos : [];

    const updateTransferencia = (patch) => {
        changeModuleConfig({
            transferencia: {
                ...transferencia,
                ...patch,
            },
        });
    };

    // Auto-migração silenciosa: se vem do banco com gatilhos/gatilhosCustom flat
    // e nenhum grupo, encapsula tudo num grupo "Padrão" e zera as chaves antigas.
    // Roda só na primeira render em que aiAgent ja chegou — evita re-disparar a migracao
    // depois que o usuario salvar e voltar.
    useEffect(() => {
        if (!aiAgent || migracaoRef.current) return;
        if (grupos.length > 0) {
            migracaoRef.current = true;
            return;
        }
        const gatilhosLegado = Array.isArray(transferencia.gatilhos)
            ? transferencia.gatilhos.filter((g) => GATILHOS_VALORES.includes(g))
            : [];
        const gatilhosCustomLegado = Array.isArray(transferencia.gatilhosCustom)
            ? transferencia.gatilhosCustom
            : [];
        if (gatilhosLegado.length === 0 && gatilhosCustomLegado.length === 0) {
            migracaoRef.current = true;
            return;
        }
        const grupoPadrao = {
            id: `grp_padrao_${Date.now()}`,
            nome: 'Padrão',
            modo,
            setor_id: setorId || null,
            usuario_destino_id: usuarioId || null,
            gatilhos: gatilhosLegado,
            gatilhosCustom: gatilhosCustomLegado,
        };
        migracaoRef.current = true;
        updateTransferencia({
            grupos: [grupoPadrao],
            gatilhos: undefined,
            gatilhosCustom: undefined,
        });
    }, [aiAgent]);

    useEffect(() => {
        async function load() {
            if (!user?.clienteId) return;
            try {
                const [setoresResp, usuariosResp] = await Promise.all([
                    v2_actionListSectorsByClient(user.clienteId),
                    getActionListaUsuariosByClienteId(user.clienteId),
                ]);
                setSetores(setoresResp?.data?.data || []);
                setUsuarios(usuariosResp?.data?.usuarios || []);
            } catch (error) {
                console.log(error);
            }
        }
        load();
    }, [user?.clienteId]);

    const updateGrupo = (grupoId, patch) => {
        updateTransferencia({
            grupos: grupos.map((g) => (g.id === grupoId ? { ...g, ...patch } : g)),
        });
    };

    const removeGrupo = (grupoId) => {
        updateTransferencia({
            grupos: grupos.filter((g) => g.id !== grupoId),
        });
    };

    const addGrupo = () => {
        updateTransferencia({ grupos: [...grupos, grupoVazio()] });
    };

    const toggleGatilhoNoGrupo = (grupoId, gatilho, checked) => {
        // Exclusividade: se outro grupo ja tem o gatilho, nao mexe.
        // O checkbox ja deveria estar desabilitado pela UI, mas validamos aqui por seguranca.
        const ownerGrupoId = gatilhosUsadosPorGrupo.get(gatilho);
        if (ownerGrupoId && ownerGrupoId !== grupoId && checked) return;
        updateTransferencia({
            grupos: grupos.map((g) => {
                if (g.id !== grupoId) return g;
                const set = new Set(g.gatilhos || []);
                if (checked) set.add(gatilho);
                else set.delete(gatilho);
                return { ...g, gatilhos: Array.from(set) };
            }),
        });
    };

    const addGatilhoCustom = (grupoId) => {
        updateTransferencia({
            grupos: grupos.map((g) => {
                if (g.id !== grupoId) return g;
                const novo = { id: novoIdGatilhoCustom(), ativo: true, nome: '', condicao: '' };
                return { ...g, gatilhosCustom: [...(g.gatilhosCustom || []), novo] };
            }),
        });
    };

    const updateGatilhoCustom = (grupoId, gatilhoId, patch) => {
        updateTransferencia({
            grupos: grupos.map((g) => {
                if (g.id !== grupoId) return g;
                return {
                    ...g,
                    gatilhosCustom: (g.gatilhosCustom || []).map((c) => (c.id === gatilhoId ? { ...c, ...patch } : c)),
                };
            }),
        });
    };

    const removeGatilhoCustom = (grupoId, gatilhoId) => {
        updateTransferencia({
            grupos: grupos.map((g) => {
                if (g.id !== grupoId) return g;
                return { ...g, gatilhosCustom: (g.gatilhosCustom || []).filter((c) => c.id !== gatilhoId) };
            }),
        });
    };

    // Map<gatilho, grupoId> — usado pra desabilitar o checkbox em outros grupos quando ja em uso.
    const gatilhosUsadosPorGrupo = new Map();
    for (const g of grupos) {
        for (const gat of (g.gatilhos || [])) {
            if (!gatilhosUsadosPorGrupo.has(gat)) gatilhosUsadosPorGrupo.set(gat, g.id);
        }
    }

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:account-arrow-right-outline" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Transferência para Consultor</CardTitle>
                        <CardDescription>Crie grupos de gatilhos com destinos diferentes (ex.: "Clientes Interessados" para o setor de vendas, "Outros Assuntos" para o SAC). Quando nenhum grupo bate, cai no destino padrão.</CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
                {/* ── Destino padrão ───────────────────────────────────────── */}
                <div className="flex flex-col gap-3">
                    <div>
                        <Label className={LABEL_CLS}>Destino padrão</Label>
                        <p className="text-xs text-gray-500 mt-1">Usado quando nenhum grupo bate o gatilho disparado (depende do comportamento de fallback abaixo).</p>
                    </div>
                    <RadioGroup
                        value={modo}
                        onValueChange={(v) => updateTransferencia({ modo: v })}
                        className="flex flex-row gap-4"
                    >
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="sector" id="default-modo-sector" />
                            <Label htmlFor="default-modo-sector" className="text-sm cursor-pointer">Setor</Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <RadioGroupItem value="user" id="default-modo-user" />
                            <Label htmlFor="default-modo-user" className="text-sm cursor-pointer">Usuário específico</Label>
                        </div>
                    </RadioGroup>

                    {modo === 'sector' && (
                        <div className="flex flex-col gap-2">
                            <Select
                                value={setorId || undefined}
                                onValueChange={(value) => updateTransferencia({ setor_id: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={setores.length === 0 ? 'Carregando setores...' : 'Selecione um setor'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {setores.map((s) => (
                                        <SelectItem key={s._id} value={s._id}>{s.nome_setor}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {modo === 'user' && (
                        <div className="flex flex-col gap-2">
                            <Select
                                value={usuarioId || undefined}
                                onValueChange={(value) => updateTransferencia({ usuario_destino_id: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder={usuarios.length === 0 ? 'Carregando usuários...' : 'Selecione um usuário'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {usuarios.map((u) => (
                                        <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                </div>

                <Separator className="bg-gray-200/60" />

                {/* ── Comportamento de fallback ────────────────────────────── */}
                <div className="flex flex-col gap-3">
                    <div>
                        <Label className={LABEL_CLS}>Quando nenhum grupo bater o gatilho</Label>
                        <p className="text-xs text-gray-500 mt-1">Define o que a Lais faz se o gatilho disparado não estiver em nenhum grupo configurado abaixo.</p>
                    </div>
                    <RadioGroup
                        value={fallback}
                        onValueChange={(v) => updateTransferencia({ fallback: v })}
                        className="flex flex-col gap-2"
                    >
                        <label
                            htmlFor="fallback-default"
                            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                fallback === 'default'
                                    ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                                    : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                            }`}
                        >
                            <RadioGroupItem value="default" id="fallback-default" className="mt-0.5" />
                            <div className="flex flex-col gap-1 flex-1">
                                <span className={`text-sm font-semibold ${fallback === 'default' ? 'text-[#7C3AED]' : 'text-gray-900'}`}>
                                    Direcionar para o destino padrão
                                </span>
                                <p className="text-xs text-gray-500 leading-relaxed">Cai no setor/usuário configurado em "Destino padrão" acima.</p>
                            </div>
                        </label>
                        <label
                            htmlFor="fallback-skip"
                            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                                fallback === 'skip'
                                    ? 'border-[#7C3AED] bg-violet-50 shadow-sm shadow-[#7C3AED]/20'
                                    : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40'
                            }`}
                        >
                            <RadioGroupItem value="skip" id="fallback-skip" className="mt-0.5" />
                            <div className="flex flex-col gap-1 flex-1">
                                <span className={`text-sm font-semibold ${fallback === 'skip' ? 'text-[#7C3AED]' : 'text-gray-900'}`}>
                                    Não transferir
                                </span>
                                <p className="text-xs text-gray-500 leading-relaxed">A Lais envia a mensagem ao cliente mas mantém o chat com ela. Use quando quiser controlar transferências apenas via grupos.</p>
                            </div>
                        </label>
                    </RadioGroup>
                </div>

                <Separator className="bg-gray-200/60" />

                {/* ── Lista de grupos ──────────────────────────────────────── */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h4 className="text-sm font-semibold text-gray-900">Grupos de gatilhos</h4>
                            <p className="text-xs text-gray-500 mt-1">Cada grupo tem seu próprio destino. Quando o cliente bate um gatilho do grupo, a transferência vai pro destino daquele grupo (e não pro padrão).</p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addGrupo}
                            className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED] shrink-0"
                        >
                            <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                            Adicionar grupo
                        </Button>
                    </div>

                    {grupos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <Iconify icon="mdi:folder-multiple-outline" width={28} className="text-gray-300" />
                            <p className="text-sm text-gray-500 mt-2 font-medium">Nenhum grupo criado</p>
                            <p className="text-xs text-gray-400 mt-1">Adicione um grupo para rotear gatilhos específicos a destinos diferentes.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {grupos.map((grupo) => (
                                <GrupoCard
                                    key={grupo.id}
                                    grupo={grupo}
                                    setores={setores}
                                    usuarios={usuarios}
                                    gatilhosUsadosPorGrupo={gatilhosUsadosPorGrupo}
                                    onUpdateGrupo={(patch) => updateGrupo(grupo.id, patch)}
                                    onRemoveGrupo={() => removeGrupo(grupo.id)}
                                    onToggleGatilho={(gat, checked) => toggleGatilhoNoGrupo(grupo.id, gat, checked)}
                                    onAddGatilhoCustom={() => addGatilhoCustom(grupo.id)}
                                    onUpdateGatilhoCustom={(gId, patch) => updateGatilhoCustom(grupo.id, gId, patch)}
                                    onRemoveGatilhoCustom={(gId) => removeGatilhoCustom(grupo.id, gId)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

function GrupoCard({
    grupo,
    setores,
    usuarios,
    gatilhosUsadosPorGrupo,
    onUpdateGrupo,
    onRemoveGrupo,
    onToggleGatilho,
    onAddGatilhoCustom,
    onUpdateGatilhoCustom,
    onRemoveGatilhoCustom,
}) {
    const modo = grupo.modo || 'sector';
    const setorId = grupo.setor_id || '';
    const usuarioId = grupo.usuario_destino_id || '';
    const gatilhos = Array.isArray(grupo.gatilhos) ? grupo.gatilhos : [];
    const gatilhosCustom = Array.isArray(grupo.gatilhosCustom) ? grupo.gatilhosCustom : [];

    return (
        <div className="flex flex-col gap-4 p-5 rounded-xl border border-violet-200 bg-violet-50/30">
            {/* Header do grupo */}
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-violet-100 shrink-0">
                    <Iconify icon="mdi:folder-outline" width={18} className="text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <Input
                        value={grupo.nome || ''}
                        onChange={(e) => onUpdateGrupo({ nome: e.target.value })}
                        placeholder="Nome do grupo (ex.: Clientes Interessados)"
                        className={`${INPUT_CLS} font-semibold bg-white`}
                    />
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onRemoveGrupo}
                    className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                    title="Remover grupo"
                >
                    <Iconify icon="eva:trash-2-outline" width={16} />
                </Button>
            </div>

            {/* Destino do grupo */}
            <div className="flex flex-col gap-3 p-4 rounded-xl bg-white border border-gray-100">
                <Label className={LABEL_CLS}>Direcionar para</Label>
                <RadioGroup
                    value={modo}
                    onValueChange={(v) => onUpdateGrupo({ modo: v })}
                    className="flex flex-row gap-4"
                >
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="sector" id={`${grupo.id}-modo-sector`} />
                        <Label htmlFor={`${grupo.id}-modo-sector`} className="text-sm cursor-pointer">Setor</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <RadioGroupItem value="user" id={`${grupo.id}-modo-user`} />
                        <Label htmlFor={`${grupo.id}-modo-user`} className="text-sm cursor-pointer">Usuário específico</Label>
                    </div>
                </RadioGroup>
                {modo === 'sector' && (
                    <Select
                        value={setorId || undefined}
                        onValueChange={(value) => onUpdateGrupo({ setor_id: value })}
                    >
                        <SelectTrigger className={INPUT_CLS}>
                            <SelectValue placeholder={setores.length === 0 ? 'Carregando setores...' : 'Selecione um setor'} />
                        </SelectTrigger>
                        <SelectContent>
                            {setores.map((s) => (
                                <SelectItem key={s._id} value={s._id}>{s.nome_setor}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                {modo === 'user' && (
                    <Select
                        value={usuarioId || undefined}
                        onValueChange={(value) => onUpdateGrupo({ usuario_destino_id: value })}
                    >
                        <SelectTrigger className={INPUT_CLS}>
                            <SelectValue placeholder={usuarios.length === 0 ? 'Carregando usuários...' : 'Selecione um usuário'} />
                        </SelectTrigger>
                        <SelectContent>
                            {usuarios.map((u) => (
                                <SelectItem key={u._id} value={u._id}>{u.nomeFantasia || u.nome || u.email}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Gatilhos predefinidos */}
            <div className="flex flex-col gap-3">
                <Label className={LABEL_CLS}>Gatilhos predefinidos</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {GATILHOS_PREDEFINIDOS.map((g) => {
                        const ativo = gatilhos.includes(g.value);
                        const ownerGrupoId = gatilhosUsadosPorGrupo.get(g.value);
                        const usadoEmOutroGrupo = !!ownerGrupoId && ownerGrupoId !== grupo.id;
                        const disabled = usadoEmOutroGrupo;
                        return (
                            <label
                                key={g.value}
                                htmlFor={`${grupo.id}-gatilho-${g.value}`}
                                className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                                    disabled
                                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                        : ativo
                                            ? 'border-[#7C3AED] bg-white shadow-sm shadow-[#7C3AED]/20 cursor-pointer'
                                            : 'border-gray-200 bg-white hover:border-violet-200 hover:bg-violet-50/40 cursor-pointer'
                                }`}
                                title={disabled ? 'Este gatilho já está em uso em outro grupo.' : undefined}
                            >
                                <Checkbox
                                    id={`${grupo.id}-gatilho-${g.value}`}
                                    checked={ativo}
                                    disabled={disabled}
                                    onCheckedChange={(checked) => onToggleGatilho(g.value, !!checked)}
                                    className="mt-0.5"
                                />
                                <div className="flex flex-col gap-1 flex-1">
                                    <div className="flex items-center gap-2">
                                        <Iconify icon={g.icon} width={16} className={ativo ? 'text-[#7C3AED]' : 'text-gray-400'} />
                                        <span className={`text-sm font-semibold ${ativo ? 'text-[#7C3AED]' : 'text-gray-900'}`}>{g.title}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 leading-relaxed">{g.description}</p>
                                    {disabled && (
                                        <p className="text-[11px] text-gray-400 italic mt-1">Em uso em outro grupo</p>
                                    )}
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Gatilhos personalizados do grupo */}
            <div className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <Label className={LABEL_CLS}>Gatilhos personalizados</Label>
                        <p className="text-xs text-gray-500 mt-1">Situações específicas em linguagem natural. Quando bater, o chat vai pro destino deste grupo.</p>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onAddGatilhoCustom}
                        className="rounded-xl border-gray-200 hover:border-[#7C3AED] hover:text-[#7C3AED] shrink-0 bg-white"
                    >
                        <Iconify icon="eva:plus-outline" width={16} className="mr-1" />
                        Adicionar
                    </Button>
                </div>

                {gatilhosCustom.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 bg-white rounded-xl border border-dashed border-gray-200">
                        <Iconify icon="mdi:lightbulb-on-outline" width={22} className="text-gray-300" />
                        <p className="text-xs text-gray-500 mt-2">Nenhum gatilho personalizado neste grupo</p>
                        <p className="text-[11px] text-gray-400 mt-1">Ex.: "cliente menciona financiamento", "cliente é investidor"</p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        {gatilhosCustom.map((c) => {
                            const ativo = c.ativo !== false;
                            return (
                                <div
                                    key={c.id}
                                    className={`flex flex-col gap-3 p-4 rounded-xl border transition-all ${
                                        ativo ? 'border-[#7C3AED]/30 bg-white' : 'border-gray-200 bg-white opacity-70'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <Checkbox
                                            checked={ativo}
                                            onCheckedChange={(checked) => onUpdateGatilhoCustom(c.id, { ativo: !!checked })}
                                            className="mt-2.5"
                                        />
                                        <div className="flex flex-col gap-2 flex-1 min-w-0">
                                            <Input
                                                value={c.nome || ''}
                                                onChange={(e) => onUpdateGatilhoCustom(c.id, { nome: e.target.value })}
                                                placeholder="Nome do gatilho (ex.: Cliente investidor)"
                                                className={`${INPUT_CLS} font-semibold`}
                                            />
                                            <Textarea
                                                value={c.condicao || ''}
                                                onChange={(e) => onUpdateGatilhoCustom(c.id, { condicao: e.target.value })}
                                                placeholder='Descreva a situação em linguagem natural. Ex.: "Cliente mencionou que é investidor, pretende comprar mais de um imóvel ou está procurando para revenda."'
                                                className={INPUT_CLS}
                                                rows={3}
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => onRemoveGatilhoCustom(c.id)}
                                            className="rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                        >
                                            <Iconify icon="eva:trash-2-outline" width={16} />
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
